import { cloneDeep, isEqual } from 'lodash';
import { combineLatest, ReplaySubject, Unsubscribable } from 'rxjs';

import { DataQuery, DataSourceRef, LoadingState } from '@grafana/schema';

import {
  AlertStateInfo,
  compareArrayValues,
  DataFrame,
  DataFrameView,
  DataQueryRequest,
  DataSourceApi,
  DataTopic,
  PanelData,
  preProcessPanelData,
  rangeUtil,
} from '@grafana/data';

// TODO: Remove this ignore annotation when the grafana runtime dependency has been updated
// @ts-ignore
import { config, getRunRequest, isExpressionReference, toDataQueryError } from '@grafana/runtime';

import { SceneObjectBase } from '../core/SceneObjectBase';
import { sceneGraph } from '../core/sceneGraph';
import {
  DataLayerFilter,
  SceneDataProvider,
  SceneDataProviderResult,
  SceneDataQuery,
  SceneObjectState,
  SceneTimeRangeLike,
} from '../core/types';
import { getDataSource } from '../utils/getDataSource';
import { VariableDependencyConfig } from '../variables/VariableDependencyConfig';
import { writeSceneLog } from '../utils/writeSceneLog';
import { VariableValueRecorder } from '../variables/VariableValueRecorder';
import { emptyPanelData } from '../core/SceneDataNode';
import { getClosest } from '../core/sceneGraph/utils';
import { ExtraQueryDataProcessor, ExtraQueryProvider, isExtraQueryProvider } from './ExtraQueryProvider';
import { extraQueryProcessingOperator, passthroughProcessor } from './extraQueryProcessingOperator';
import { filterAnnotations } from './layers/annotations/filterAnnotations';
import { getEnrichedDataRequest } from './getEnrichedDataRequest';
import { QueryProfilerLike, registerQueryWithController } from './registerQueryWithController';
import { findPanelProfiler } from '../utils/findPanelProfiler';
import { AdHocFiltersVariable } from '../variables/adhoc/AdHocFiltersVariable';
import { GroupByVariable } from '../variables/groupby/GroupByVariable';
import { SceneVariable } from '../variables/types';
import { DataLayersMerger } from './DataLayersMerger';
import { interpolate } from '../core/sceneGraph/sceneGraph';
import { wrapInSafeSerializableSceneObject } from '../utils/wrapInSafeSerializableSceneObject';
import { DrilldownDependenciesManager } from '../variables/DrilldownDependenciesManager';

let counter = 100;
const MIXED_DATASOURCE_UID = '-- Mixed --';
const MIXED_DATASOURCE_REF: DataSourceRef = { type: 'mixed', uid: MIXED_DATASOURCE_UID };

export function getNextRequestId(prefix = 'SQR') {
  return prefix + counter++;
}

export interface QueryRunnerState extends SceneObjectState {
  data?: PanelData;
  queries: SceneDataQuery[];
  datasource?: DataSourceRef;
  minInterval?: string;
  maxDataPoints?: number;
  liveStreaming?: boolean;
  maxDataPointsFromWidth?: boolean;
  cacheTimeout?: DataQueryRequest['cacheTimeout'];
  queryCachingTTL?: DataQueryRequest['queryCachingTTL'];
  /**
   * When set to auto (the default) query runner will issue queries on activate (when variable dependencies are ready) or when time range change.
   * Set to manual to have full manual control over when queries are issued. Try not to set this. This is mainly useful for unit tests, or special edge case workflows.
   */
  runQueriesMode?: 'auto' | 'manual';
  // Filters to be applied to data layer results before combining them with SQR results
  dataLayerFilter?: DataLayerFilter;
  /**
   * Optional prefix for the requestId. When set, request IDs will be formatted as `{requestIdPrefix}{counter}`.
   * Useful for identifying requests from specific panels or components.
   */
  requestIdPrefix?: string;
  // Private runtime state
  _hasFetchedData?: boolean;
}

function isTemplatedDatasourceUid(uid: string | undefined): boolean {
  if (!uid) {
    return false;
  }

  // Detect any variable template usage in a datasource UID so we can evaluate runtime fan-out.
  return /^(?:\$[A-Za-z0-9_]+|\$\{[^}]+\})/.test(uid);
}

function isTemplatedDatasourceRef(datasource: DataSourceRef | null | undefined): boolean {
  return isTemplatedDatasourceUid(datasource?.uid);
}

function getSimpleVariableNameFromDatasourceUid(uid: string | undefined): string | undefined {
  if (!uid) {
    return undefined;
  }
  const shortMatch = uid.match(/^\$([A-Za-z0-9_]+)$/);
  if (shortMatch) {
    return shortMatch[1];
  }

  const bracketMatch = uid.match(/^\$\{([A-Za-z0-9_]+)\}$/);
  if (bracketMatch) {
    return bracketMatch[1];
  }

  return undefined;
}

function getDatasourceVariableSelectionCount(sceneObject: SceneQueryRunner, variableName: string): number | undefined {
  const variable = sceneGraph.lookupVariable(variableName, sceneObject);
  if (!variable) {
    return undefined;
  }

  const value = variable.getValue();
  if (Array.isArray(value)) {
    return value.filter((item) => item !== 'default').length;
  }

  if (value === null || value === undefined) {
    return undefined;
  }

  return value === 'default' ? 0 : 1;
}

function shouldUseMixedRuntimeDatasource(
  sceneObject: SceneQueryRunner,
  datasource: DataSourceRef | null | undefined,
  queries: SceneDataQuery[]
) {
  // Check both panel datasource and per-query datasources.
  const refs: Array<DataSourceRef | null | undefined> = [datasource, ...queries.map((query) => query.datasource)];

  for (const ref of refs) {
    const uid = ref?.uid;
    if (!uid) {
      continue;
    }

    const variableName = getSimpleVariableNameFromDatasourceUid(uid);
    if (variableName) {
      const selectionCount = getDatasourceVariableSelectionCount(sceneObject, variableName);

      if (selectionCount !== undefined && selectionCount > 1) {
        return true;
      }

      continue;
    }
  }

  return false;
}

function parseRefsFromMathExpression(input: string): string[] {
  const bracket = /\$\{([A-Za-z0-9_ ]+?)\}/gm;
  const simple = /\$([A-Za-z0-9_]+)/gm;
  const found = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = bracket.exec(input)) !== null) {
    found.add(match[1]);
  }
  while ((match = simple.exec(input)) !== null) {
    found.add(match[1]);
  }
  return Array.from(found);
}

// Returns the refIds a server-side expression query depends on.
function getExpressionReferences(query: DataQueryExtended): string[] {
  const { type, expression, conditions } = query;
  if (type === 'math' || type === 'sql') {
    return parseRefsFromMathExpression(expression ?? '');
  }
  if (type === 'classic_conditions') {
    return (conditions ?? [])
      .map((condition: { query?: { params?: string[] } }) => condition.query?.params?.[0])
      .filter((refId: string | undefined): refId is string => Boolean(refId));
  }
  // reduce / resample / threshold: the whole `expression` is a single refId
  return expression ? [expression] : [];
}

// Renames the refIds a server-side expression references, according to renameMap.
function rewriteExpressionReferences(query: DataQueryExtended, renameMap: Record<string, string>): void {
  const { type } = query;
  if (type === 'math' || type === 'sql') {
    let expression: string = query.expression ?? '';
    for (const oldRef of Object.keys(renameMap)) {
      const pattern = new RegExp('(\\$' + oldRef + '\\b)|(\\$\\{' + oldRef + '\\})', 'gm');
      expression = expression.replace(pattern, '${' + renameMap[oldRef] + '}');
    }
    query.expression = expression;
  } else if (type === 'classic_conditions') {
    (query.conditions ?? []).forEach((condition: { query?: { params?: string[] } }) => {
      const params = condition.query?.params;
      if (params && params[0] && renameMap[params[0]]) {
        params[0] = renameMap[params[0]];
      }
    });
  } else if (query.expression && renameMap[query.expression]) {
    query.expression = renameMap[query.expression];
  }
}

/**
 * When a data query using a multi-value datasource variable feeds a server-side expression, the
 * whole graph must be evaluated once per selected datasource. We fan out by cloning the data query
 * (pinned to a concrete datasource) and its dependent expression subgraph once per selected value,
 * rewriting refIds so each replica is self-contained. Queries and expressions that do not depend on
 * the multi-value variable are left untouched (they run once).
 *
 * Only a single multi-value datasource variable shared by the data queries is supported.
 */
function expandExpressionFanOut(sceneObject: SceneQueryRunner, targets: DataQueryExtended[]): DataQueryExtended[] {
  const isExpression = (query: DataQueryExtended) =>
    Boolean(isExpressionReference && isExpressionReference(query.datasource));

  if (!targets.some(isExpression)) {
    return targets;
  }

  // Find the multi-value datasource variable used by a data query (if any).
  let variableName: string | undefined;
  let uids: string[] | undefined;
  for (const target of targets) {
    if (isExpression(target)) {
      continue;
    }
    const candidate = getSimpleVariableNameFromDatasourceUid(target.datasource?.uid);
    if (!candidate) {
      continue;
    }
    const value = sceneGraph.lookupVariable(candidate, sceneObject)?.getValue();
    if (Array.isArray(value)) {
      const selected = value.filter((item) => item !== 'default').map(String);
      if (selected.length > 1) {
        variableName = candidate;
        uids = selected;
        break;
      }
    }
  }

  if (!variableName || !uids) {
    return targets;
  }

  // Data queries using the multi-value variable are the fan-out roots.
  const fannedRootRefIds = new Set<string>();
  for (const target of targets) {
    if (!isExpression(target) && getSimpleVariableNameFromDatasourceUid(target.datasource?.uid) === variableName) {
      fannedRootRefIds.add(target.refId);
    }
  }

  // Transitive closure of expressions that (transitively) depend on a fan-out root.
  const dependentRefIds = new Set<string>(fannedRootRefIds);
  let changed = true;
  while (changed) {
    changed = false;
    for (const target of targets) {
      if (!isExpression(target) || dependentRefIds.has(target.refId)) {
        continue;
      }
      if (getExpressionReferences(target).some((refId) => dependentRefIds.has(refId))) {
        dependentRefIds.add(target.refId);
        changed = true;
      }
    }
  }

  const expanded: DataQueryExtended[] = targets.filter((target) => !dependentRefIds.has(target.refId));
  const toFanOut = targets.filter((target) => dependentRefIds.has(target.refId));

  uids.forEach((uid, index) => {
    const renameMap: Record<string, string> = {};
    for (const target of toFanOut) {
      renameMap[target.refId] = `${target.refId}_${index}`;
    }
    for (const target of toFanOut) {
      const clone = cloneDeep(target);
      clone.refId = renameMap[target.refId];
      if (fannedRootRefIds.has(target.refId)) {
        clone.datasource = { ...clone.datasource, uid };
      } else {
        rewriteExpressionReferences(clone, renameMap);
      }
      expanded.push(clone);
    }
  });

  return expanded;
}

export interface DataQueryExtended extends DataQuery {
  [key: string]: any;

  // Opt this query out of time window comparison
  timeRangeCompare?: boolean;
}

// The requests that will be run by the query runner.
//
// Generally the query runner will run a single primary request.
// If the scene graph contains implementations of
// `ExtraQueryProvider`, the requests created by these
// implementations will be added to the list of secondary requests,
// and these will be executed at the same time as the primary request.
//
// The results of each secondary request will be passed to an associated
// processor function (along with the results of the primary request),
// which can transform the results as desired.
interface PreparedRequests {
  // The primary request to run.
  primary: DataQueryRequest;
  // A possibly empty list of secondary requests to run alongside
  // the primary request.
  secondaries: DataQueryRequest[];
  // A map from `requestId` of secondary requests to processors
  // for those requests. Provided by the `ExtraQueryProvider`.
  processors: Map<string, ExtraQueryDataProcessor>;
}

export class SceneQueryRunner extends SceneObjectBase<QueryRunnerState> implements SceneDataProvider {
  private _querySub?: Unsubscribable;
  private _dataLayersSub?: Unsubscribable;
  private _dataLayersMerger = new DataLayersMerger();
  private _timeSub?: Unsubscribable;
  private _timeSubRange?: SceneTimeRangeLike;
  private _containerWidth?: number;
  private _variableValueRecorder = new VariableValueRecorder();
  private _results = new ReplaySubject<SceneDataProviderResult>(1);
  private _scopedVars = { __sceneObject: wrapInSafeSerializableSceneObject(this) };
  private _layerAnnotations?: DataFrame[];
  private _resultAnnotations?: DataFrame[];
  private _isInView = true;
  private _bypassIsInView = false;
  private _queryNotExecutedWhenOutOfView = false;

  public getResultsStream() {
    return this._results;
  }

  protected _variableDependency: VariableDependencyConfig<QueryRunnerState> = new VariableDependencyConfig(this, {
    statePaths: ['queries', 'datasource', 'minInterval'],
    onVariableUpdateCompleted: this.onVariableUpdatesCompleted.bind(this),
    onAnyVariableChanged: this.onAnyVariableChanged.bind(this),
    dependsOnScopes: true,
  });

  private _drilldownDependenciesManager: DrilldownDependenciesManager<QueryRunnerState> =
    new DrilldownDependenciesManager(this._variableDependency);

  public constructor(initialState: QueryRunnerState) {
    super(initialState);

    this.addActivationHandler(() => this._onActivate());
  }

  private _onActivate() {
    if (this.isQueryModeAuto()) {
      const timeRange = sceneGraph.getTimeRange(this);

      // Add subscriptions to any extra providers so that they rerun queries
      // when their state changes and they should rerun.
      const providers = this.getClosestExtraQueryProviders();
      for (const provider of providers) {
        this._subs.add(
          provider.subscribeToState((n, p) => {
            if (provider.shouldRerun(p, n, this.state.queries)) {
              this.runQueries();
            }
          })
        );
      }

      this.subscribeToTimeRangeChanges(timeRange);

      if (this.shouldRunQueriesOnActivate()) {
        this.runQueries();
      }
    }

    if (!this._dataLayersSub) {
      this._handleDataLayers();
    }

    return () => this._onDeactivate();
  }

  // This method subscribes to all SceneDataLayers up until the root, and combines the results into data provided from SceneQueryRunner
  private _handleDataLayers() {
    const dataLayers = sceneGraph.getDataLayers(this);

    if (dataLayers.length === 0) {
      return;
    }

    this._dataLayersSub = this._dataLayersMerger
      .getMergedStream(dataLayers)
      .subscribe(this._onLayersReceived.bind(this));
  }

  private _onLayersReceived(results: Iterable<SceneDataProviderResult>) {
    const timeRange = sceneGraph.getTimeRange(this);
    const { dataLayerFilter } = this.state;

    let annotations: DataFrame[] = [];
    let alertStates: DataFrame[] = [];
    let alertState: AlertStateInfo | undefined;

    for (const result of results) {
      for (let frame of result.data.series) {
        if (frame.meta?.dataTopic === DataTopic.Annotations) {
          annotations = annotations.concat(frame);
        }
        if (frame.meta?.dataTopic === DataTopic.AlertStates) {
          alertStates = alertStates.concat(frame);
        }
      }
    }

    if (dataLayerFilter?.panelId) {
      if (annotations.length > 0) {
        annotations = filterAnnotations(annotations, dataLayerFilter);
      }

      if (alertStates.length > 0) {
        for (const frame of alertStates) {
          const frameView = new DataFrameView<AlertStateInfo>(frame);

          for (const row of frameView) {
            if (row.panelId === dataLayerFilter.panelId) {
              alertState = row;
              break;
            }
          }
        }
      }
    }

    // Skip unnessary state updates
    if (
      allFramesEmpty(annotations) &&
      allFramesEmpty(this._layerAnnotations) &&
      isEqual(alertState, this.state.data?.alertState)
    ) {
      return;
    }

    this._layerAnnotations = annotations;

    const baseStateUpdate = this.state.data ? this.state.data : { ...emptyPanelData, timeRange: timeRange.state.value };

    this.setState({
      data: {
        ...baseStateUpdate,
        annotations: [...(this._resultAnnotations ?? []), ...annotations],
        alertState: alertState ?? this.state.data?.alertState,
      },
    });
  }

  /**
   * This tries to start a new query whenever a variable completes or is changed.
   *
   * We care about variable update completions even when the variable has not changed and even when it is not a direct dependency.
   * Example: Variables A and B (B depends on A). A update depends on time range. So when time change query runner will
   * find that variable A is loading which is a dependency on of variable B so will set _isWaitingForVariables to true and
   * not issue any query.
   *
   * When A completes it's loading (with no value change, so B never updates) it will cause a call of this function letting
   * the query runner know that A has completed, and in case _isWaitingForVariables we try to run the query. The query will
   * only run if all variables are in a non loading state so in other scenarios where a query depends on many variables this will
   * be called many times until all dependencies are in a non loading state.   *
   */
  private onVariableUpdatesCompleted() {
    if (this.isQueryModeAuto()) {
      this.runQueries();
    }
  }

  /**
   * Check if value changed is an adhoc filter or group by variable that did not exist when we issued the last query
   */
  private onAnyVariableChanged(variable: SceneVariable) {
    if (
      this._drilldownDependenciesManager.adHocFiltersVar === variable ||
      this._drilldownDependenciesManager.groupByVar === variable ||
      !this.isQueryModeAuto()
    ) {
      return;
    }

    if (variable instanceof AdHocFiltersVariable && this._isRelevantAutoVariable(variable)) {
      this.runQueries();
    }

    if (variable instanceof GroupByVariable && this._isRelevantAutoVariable(variable)) {
      this.runQueries();
    }
  }

  private _isRelevantAutoVariable(variable: AdHocFiltersVariable | GroupByVariable) {
    const datasource = this.state.datasource ?? findFirstDatasource(this.state.queries);
    return variable.state.applyMode === 'auto' && datasource?.uid === variable.state.datasource?.uid;
  }

  private shouldRunQueriesOnActivate() {
    if (this._variableValueRecorder.hasDependenciesChanged(this)) {
      writeSceneLog(
        'SceneQueryRunner',
        'Variable dependency changed while inactive, shouldRunQueriesOnActivate returns true'
      );
      return true;
    }

    // If we don't have any data we should run queries
    if (!this.state.data) {
      return true;
    }

    // If time range is stale / different we should run queries
    if (this._isDataTimeRangeStale(this.state.data)) {
      return true;
    }

    return false;
  }

  private _isDataTimeRangeStale(data: PanelData) {
    const timeRange = sceneGraph.getTimeRange(this);

    const stateTimeRange = timeRange.state.value;
    const dataTimeRange = data.timeRange;

    if (
      stateTimeRange.from.unix() === dataTimeRange.from.unix() &&
      stateTimeRange.to.unix() === dataTimeRange.to.unix()
    ) {
      return false;
    }
    writeSceneLog('SceneQueryRunner', 'Data time range is stale');
    return true;
  }

  private _onDeactivate(): void {
    if (this._querySub) {
      this._querySub.unsubscribe();
      this._querySub = undefined;
    }

    if (this._dataLayersSub) {
      this._dataLayersSub.unsubscribe();
      this._dataLayersSub = undefined;
    }

    this._timeSub?.unsubscribe();
    this._timeSub = undefined;
    this._timeSubRange = undefined;

    // Reset data to its initial state unless it reached a terminal state (Done or Error).
    // Incomplete data is discarded so reactivation starts with a clean state.
    if (this.state.data && ![LoadingState.Done, LoadingState.Error].includes(this.state.data.state)) {
      this.setState({ data: undefined });
    }

    this._drilldownDependenciesManager.cleanup();
  }

  public setContainerWidth(width: number) {
    // If we don't have a width we should run queries
    if (!this._containerWidth && width > 0) {
      this._containerWidth = width;

      // If we don't have maxDataPoints specifically set and maxDataPointsFromWidth is true
      if (this.state.maxDataPointsFromWidth && !this.state.maxDataPoints) {
        // As this is called from render path we need to wait for next tick before running queries
        setTimeout(() => {
          if (this.isActive && !this.state._hasFetchedData) {
            this.runQueries();
          }
        }, 0);
      }
    } else {
      // if the updated container width is bigger than 0 let's remember the width until next query issue
      if (width > 0) {
        this._containerWidth = width;
      }
    }
  }

  public isDataReadyToDisplay() {
    return Boolean(this.state._hasFetchedData);
  }

  private subscribeToTimeRangeChanges(timeRange: SceneTimeRangeLike) {
    if (this._timeSubRange === timeRange) {
      // Nothing to do, already subscribed
      return;
    }

    if (this._timeSub) {
      this._timeSub.unsubscribe();
    }

    this._timeSubRange = timeRange;
    this._timeSub = timeRange.subscribeToState(() => {
      // setTimeout to let SceneVariableSet also respond to time range change
      // So that variables that depend on time range have time to switch to loading state
      setTimeout(() => this.runWithTimeRange(timeRange), 0);
    });
  }

  public runQueries() {
    const timeRange = sceneGraph.getTimeRange(this);

    if (this.isQueryModeAuto()) {
      this.subscribeToTimeRangeChanges(timeRange);
    }

    this.runWithTimeRange(timeRange);
  }

  private getMaxDataPoints() {
    if (this.state.maxDataPoints) {
      return this.state.maxDataPoints;
    }

    return this.state.maxDataPointsFromWidth ? this._containerWidth ?? 500 : 500;
  }

  public cancelQuery() {
    this._querySub?.unsubscribe();

    if (this._dataLayersSub) {
      this._dataLayersSub.unsubscribe();
      this._dataLayersSub = undefined;
    }

    this.setState({
      data: { ...(this.state.data ?? emptyPanelData), state: LoadingState.Done },
    });
  }

  private async runWithTimeRange(timeRange: SceneTimeRangeLike) {
    // If no maxDataPoints specified we might need to wait for container width to be set from the outside
    if (!this.state.maxDataPoints && this.state.maxDataPointsFromWidth && !this._containerWidth) {
      return;
    }

    if (this.isQueryModeAuto() && !this._isInView && !this._bypassIsInView) {
      this._queryNotExecutedWhenOutOfView = true;
      return;
    }

    this._queryNotExecutedWhenOutOfView = false;

    // If data layers subscription doesn't exist, create one
    if (!this._dataLayersSub) {
      this._handleDataLayers();
    }

    // Cancel any running queries
    this._querySub?.unsubscribe();

    // Skip executing queries if variable dependency is in loading state
    if (this._variableDependency.hasDependencyInLoadingState()) {
      writeSceneLog('SceneQueryRunner', 'Variable dependency is in loading state, skipping query execution');
      this.setState({ data: { ...(this.state.data ?? emptyPanelData), state: LoadingState.Loading } });
      return;
    }

    this._variableValueRecorder.recordCurrentDependencyValuesForSceneObject(this);

    const { queries } = this.state;

    // Simple path when no queries exist
    if (!queries?.length) {
      this._setNoDataState();
      return;
    }

    try {
      const datasource = this.state.datasource ?? findFirstDatasource(queries);
      const runtimeDatasource = shouldUseMixedRuntimeDatasource(this, datasource, queries)
        ? MIXED_DATASOURCE_REF
        : datasource;
      const ds = await getDataSource(runtimeDatasource, this._scopedVars);

      this._drilldownDependenciesManager.findAndSubscribeToDrilldowns(ds.uid, this);

      const runRequest = getRunRequest();
      const { primary, secondaries, processors } = this.prepareRequests(timeRange, ds);

      writeSceneLog('SceneQueryRunner', 'Starting runRequest', this.state.key);

      let stream = runRequest(ds, primary);

      if (secondaries.length > 0) {
        // Submit all secondary requests in parallel.
        const secondaryStreams = secondaries.map((r) => runRequest(ds, r));
        // Create the rxjs operator which will combine the primary and secondary responses
        // by calling the correct processor functions provided by the
        // extra request providers.
        const op = extraQueryProcessingOperator(processors);
        // Combine the primary and secondary streams into a single stream, and apply the operator.
        // Use combineLatest so intermediate loading/error states from the primary and secondary requests are emitted as
        // they arrive, instead of only a single combined emission once every request has completed.
        stream = combineLatest([stream, ...secondaryStreams]).pipe(op);
      }

      const panelProfiler: QueryProfilerLike | undefined = findPanelProfiler(this);

      stream = stream.pipe(
        registerQueryWithController(
          {
            type: 'SceneQueryRunner/runQueries',
            request: primary,
            origin: this,
            cancel: () => this.cancelQuery(),
          },
          panelProfiler
        )
      );

      this._querySub = stream.subscribe(this.onDataReceived);
    } catch (err) {
      console.error('PanelQueryRunner Error', err);

      this.onDataReceived({
        ...emptyPanelData,
        ...this.state.data,
        state: LoadingState.Error,
        errors: [toDataQueryError(err)],
      });
    }
  }

  public clone(withState?: Partial<QueryRunnerState>) {
    const clone = super.clone(withState);

    if (this._resultAnnotations) {
      clone['_resultAnnotations'] = this._resultAnnotations.map((frame) => ({ ...frame }));
    }

    if (this._layerAnnotations) {
      clone['_layerAnnotations'] = this._layerAnnotations.map((frame) => ({ ...frame }));
    }

    clone['_variableValueRecorder'] = this._variableValueRecorder.cloneAndRecordCurrentValuesForSceneObject(this);
    clone['_containerWidth'] = this._containerWidth;
    clone['_results'].next({ origin: this, data: this.state.data ?? emptyPanelData });

    return clone;
  }

  private prepareRequests(timeRange: SceneTimeRangeLike, ds: DataSourceApi): PreparedRequests {
    const { minInterval, queries, requestIdPrefix } = this.state;

    let request: DataQueryRequest<DataQueryExtended> = {
      app: 'scenes',
      requestId: getNextRequestId(requestIdPrefix),
      timezone: timeRange.getTimeZone(),
      range: timeRange.state.value,
      interval: '1s',
      intervalMs: 1000,
      targets: cloneDeep(queries),
      maxDataPoints: this.getMaxDataPoints(),
      scopedVars: this._scopedVars,
      startTime: Date.now(),
      liveStreaming: this.state.liveStreaming,
      rangeRaw: {
        from: timeRange.state.from,
        to: timeRange.state.to,
      },
      cacheTimeout: this.state.cacheTimeout,
      queryCachingTTL: this.state.queryCachingTTL,
      scopes: sceneGraph.getScopes(this),
      // This asks the scene root to provide context properties like app, panel and dashboardUID
      ...getEnrichedDataRequest(this),
    };

    const filters = this._drilldownDependenciesManager.getFilters();
    const groupByKeys = this._drilldownDependenciesManager.getGroupByKeys();

    if (filters) {
      request.filters = filters;
    }

    if (groupByKeys) {
      request.groupByKeys = groupByKeys;
    }

    request.targets = request.targets.map((query) => {
      if (
        !query.datasource ||
        (query.datasource.uid !== ds.uid &&
          !ds.meta?.mixed &&
          isExpressionReference /* TODO: Remove this check when isExpressionReference is properly exported from grafan runtime */ &&
          !isExpressionReference(query.datasource))
      ) {
        if (!query.datasource && ds.meta?.mixed && isTemplatedDatasourceRef(this.state.datasource)) {
          query.datasource = this.state.datasource;
        } else {
          query.datasource = ds.getRef();
        }
      }
      return query;
    });

    request.targets = expandExpressionFanOut(this, request.targets);

    const lowerIntervalLimit = minInterval ? interpolate(this, minInterval) : ds.interval;
    const norm = rangeUtil.calculateInterval(timeRange.state.value, request.maxDataPoints!, lowerIntervalLimit);

    // make shallow copy of scoped vars,
    // and add built in variables interval and interval_ms
    request.scopedVars = Object.assign({}, request.scopedVars, {
      __interval: { text: norm.interval, value: norm.interval },
      __interval_ms: { text: norm.intervalMs.toString(), value: norm.intervalMs },
    });

    request.interval = norm.interval;
    request.intervalMs = norm.intervalMs;

    // If there are any extra request providers, we need to add a new request for each
    // and map the request's ID to the processor function given by the provider, to ensure that
    // the processor is called with the correct response data.
    const primaryTimeRange = timeRange.state.value;
    let secondaryRequests: DataQueryRequest[] = [];
    let secondaryProcessors = new Map();
    for (const provider of this.getClosestExtraQueryProviders() ?? []) {
      for (const { req, processor } of provider.getExtraQueries(request)) {
        const requestId = getNextRequestId(requestIdPrefix);
        secondaryRequests.push({ ...req, requestId });
        secondaryProcessors.set(requestId, processor ?? passthroughProcessor);
      }
    }
    request.range = primaryTimeRange;
    return { primary: request, secondaries: secondaryRequests, processors: secondaryProcessors };
  }

  private onDataReceived = (data: PanelData) => {
    const preProcessedData = preProcessPanelData(data, this.state.data);

    this._resultAnnotations = data.annotations;

    const dataWithLayersApplied = this._combineDataLayers(preProcessedData);

    const last = this.state.data;

    // Restore referential identity of series/annotations when frames haven't changed,
    // preventing panels from reprocessing unchanged data. Brings back logic that existed in PanelQueryRunner in core.
    if (last != null && dataWithLayersApplied.state !== LoadingState.Streaming) {
      const sameSeries = compareArrayValues(last.series ?? [], dataWithLayersApplied.series ?? [], (a, b) => a === b);
      const sameAnnotations = compareArrayValues(
        last.annotations ?? [],
        dataWithLayersApplied.annotations ?? [],
        (a, b) => a === b
      );
      const sameState = last.state === dataWithLayersApplied.state;
      const sameErrors = compareArrayValues(last.errors ?? [], dataWithLayersApplied.errors ?? [], (a, b) =>
        isEqual(a, b)
      );

      if (sameSeries) {
        dataWithLayersApplied.series = last.series;
      }

      if (sameAnnotations) {
        dataWithLayersApplied.annotations = last.annotations;
      }

      if (sameSeries && sameAnnotations && sameState && sameErrors) {
        if (last.request?.requestId === dataWithLayersApplied.request?.requestId) {
          return;
        }
      }
    }

    let hasFetchedData = this.state._hasFetchedData;

    if (!hasFetchedData && preProcessedData.state !== LoadingState.Loading) {
      hasFetchedData = true;
    }

    this.setState({ data: dataWithLayersApplied, _hasFetchedData: hasFetchedData });
    this._results.next({ origin: this, data: dataWithLayersApplied });
  };

  private _combineDataLayers(data: PanelData) {
    if (this._layerAnnotations && this._layerAnnotations.length > 0) {
      data.annotations = (data.annotations || []).concat(this._layerAnnotations);
    }

    if (this.state.data && this.state.data.alertState) {
      data.alertState = this.state.data.alertState;
    }

    return data;
  }

  private _setNoDataState() {
    if (this.state.data !== emptyPanelData) {
      this.setState({ data: emptyPanelData });
    }
  }

  /**
   * Walk up the scene graph and find any ExtraQueryProviders.
   *
   * This will return an array of the closest provider of each type.
   */
  private getClosestExtraQueryProviders(): Array<ExtraQueryProvider<any>> {
    // Maintain a map from provider constructor to provider object. The constructor
    // is used as a unique key for each class, to ensure we have no more than one
    // type of each type of provider.
    const found = new Map();
    if (!this.parent) {
      return [];
    }
    getClosest(this.parent, (s) => {
      if (isExtraQueryProvider(s) && !found.has(s.constructor)) {
        found.set(s.constructor, s);
      }
      s.forEachChild((child) => {
        if (isExtraQueryProvider(child) && !found.has(child.constructor)) {
          found.set(child.constructor, child);
        }
      });
      // Always return null so that the search continues to the top of
      // the scene graph.
      return null;
    });
    return Array.from(found.values());
  }

  private isQueryModeAuto(): boolean {
    return (this.state.runQueriesMode ?? 'auto') === 'auto';
  }

  public isInViewChanged(isInView: boolean): void {
    writeSceneLog('SceneQueryRunner', `isInViewChanged: ${isInView}`, this.state.key);
    this._isInView = isInView;

    if (isInView && this._queryNotExecutedWhenOutOfView) {
      this.runQueries();
    }
  }

  public bypassIsInViewChanged(bypassIsInView: boolean): void {
    writeSceneLog('SceneQueryRunner', `bypassIsInViewChanged: ${bypassIsInView}`, this.state.key);
    this._bypassIsInView = bypassIsInView;

    if (bypassIsInView && this._queryNotExecutedWhenOutOfView) {
      this.runQueries();
    }
  }
}

export function findFirstDatasource(targets: DataQuery[]): DataSourceRef | undefined {
  return targets.find((t) => t.datasource !== null)?.datasource ?? undefined;
}

function allFramesEmpty(frames?: DataFrame[]) {
  if (!frames) {
    return true;
  }

  for (let i = 0; i < frames.length; i++) {
    if (frames[i].length > 0) {
      return false;
    }
  }

  return true;
}
